<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeModal"></div>
      
      <!-- Modal -->
      <div class="relative bg-casino-card border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">
        <!-- Header -->
        <div class="p-6 border-b border-white/5">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <img src="/todofino_icon.png" alt="TodoFino" class="w-10 h-10 rounded-xl object-cover" />
              <div>
                <h2 class="text-xl font-bold text-white">{{ otpStep ? t('auth.verifyEmailTitle') : t('auth.welcome') }}</h2>
                <p class="text-sm text-gray-400">{{ otpStep ? t('auth.enterOtpToContinue') : t('auth.loginToContinue') }}</p>
              </div>
            </div>
            <button @click="closeModal" class="btn-icon p-2">
              <X class="w-5 h-5" />
            </button>
          </div>
        </div>

        <!-- Form -->
        <div class="p-6">
          <form @submit.prevent="otpStep ? handleVerifyOtp() : handleLogin()" class="space-y-5">
            <!-- Error -->
            <div v-if="authStore.error" class="bg-casino-loss/10 border border-casino-loss/30 rounded-lg p-3">
              <p class="text-casino-loss text-sm">{{ authStore.error }}</p>
            </div>

            <!-- Username -->
            <div v-if="!otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.username') }}</label>
              <div class="relative">
                <AtSign class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  v-model="form.username"
                  type="text"
                  required
                  class="input pl-10"
                  :placeholder="t('auth.usernamePlaceholder')"
                  :disabled="loading"
                />
              </div>
            </div>

            <!-- Password -->
            <div v-if="!otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.password') }}</label>
              <div class="relative">
                <Lock class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  class="input pl-10 pr-10"
                  placeholder="••••••••"
                  :disabled="loading"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                >
                  <Eye v-if="!showPassword" class="w-5 h-5" />
                  <EyeOff v-else class="w-5 h-5" />
                </button>
              </div>
            </div>

            <!-- Options -->
            <div v-if="!otpStep" class="flex items-center justify-between">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="form.remember"
                  type="checkbox"
                  class="w-4 h-4 rounded border-white/20 bg-white/5 text-casino-primary focus:ring-casino-primary"
                />
                <span class="text-sm text-gray-400">{{ t('auth.rememberMe') }}</span>
              </label>
              <button
                type="button"
                class="text-sm text-casino-primary hover:text-casino-primary-light"
                @click="goToRecovery"
              >
                {{ t('auth.forgotPassword') }}
              </button>
            </div>


            <!-- OTP -->
            <div v-if="otpStep">
              <label class="block text-sm font-medium text-gray-300 mb-2">{{ t('auth.otpCode') }}</label>
              <OtpCodeInput v-model="otpCode" :disabled="loading" />
              <p v-if="otpRemainingSeconds > 0" class="mt-2 text-xs text-gray-500">
                {{ t('auth.resendIn', { time: formatSeconds(otpRemainingSeconds) }) }}
              </p>
              <div class="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  class="text-sm text-casino-primary hover:text-casino-primary-light disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-casino-primary"
                  :disabled="loading || otpRemainingSeconds > 0"
                  @click="handleResendOtp"
                >
                  {{ t('auth.resendCode') }}
                </button>
                <button
                  type="button"
                  class="text-sm text-casino-primary hover:text-casino-primary-light"
                  :disabled="loading"
                  @click="resetOtpStep"
                >
                  {{ t('common.back') }}
                </button>
              </div>
            </div>
            <!-- Submit -->
            <button
              type="submit"
              :disabled="loading || (otpStep && String(otpCode || '').replace(/\\D/g, '').length !== 6)"
              class="w-full py-3 bg-casino-primary hover:bg-casino-primary-light text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Loader2 v-if="loading" class="w-5 h-5 animate-spin" />
              <LogIn v-else class="w-5 h-5" />
              {{ loading ? (otpStep ? t('auth.verifying') : t('auth.loggingIn')) : (otpStep ? t('auth.verifyCode') : t('auth.login')) }}
            </button>
          </form>

          <!-- Divider -->
          <div v-if="!otpStep" class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-white/10"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-casino-card text-gray-500">{{ t('auth.orContinueWith') }}</span>
            </div>
          </div>

          <!-- Social Login -->
          <div v-if="!otpStep" class="grid grid-cols-2 gap-3">
            <button class="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm text-gray-300">
              <span class="text-lg">G</span>
              Google
            </button>
            <button class="flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-sm text-gray-300">
              <span class="text-lg">𝕏</span>
              Twitter
            </button>
          </div>

          <!-- Register Link -->
          <p v-if="!otpStep" class="mt-6 text-center text-sm text-gray-400">
            {{ t('auth.noAccount') }}
            <button
              type="button"
              @click="switchToRegister"
              class="text-casino-primary hover:text-casino-primary-light font-medium ml-1"
            >
              {{ t('auth.register') }}
            </button>
          </p>

        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import OtpCodeInput from '@/components/OtpCodeInput.vue'
import { 
  X, 
  Eye, 
  EyeOff, 
  LogIn, 
  Loader2,
  AtSign,
  Lock
} from 'lucide-vue-next'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'switchToRegister', 'success'])

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const form = ref({
  username: '',
  password: '',
  remember: false
})
const showPassword = ref(false)
const loading = ref(false)
const otpStep = ref(false)
const otpToken = ref('')
const otpCode = ref('')
const otpExpiresAt = ref(0)
const otpRemainingSeconds = ref(0)
let otpTimer = null

const formatSeconds = (value) => {
  const total = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const stopOtpTimer = () => {
  if (otpTimer) {
    clearInterval(otpTimer)
    otpTimer = null
  }
}

const updateOtpRemaining = () => {
  if (!otpExpiresAt.value) {
    otpRemainingSeconds.value = 0
    return
  }
  otpRemainingSeconds.value = Math.max(0, Math.ceil((otpExpiresAt.value - Date.now()) / 1000))
  if (otpRemainingSeconds.value <= 0) {
    stopOtpTimer()
  }
}

const startOtpTimer = ({ expiresInSeconds }) => {
  const ttlSeconds = Number(expiresInSeconds)
  otpExpiresAt.value = Number.isFinite(ttlSeconds) && ttlSeconds > 0
    ? Date.now() + Math.floor(ttlSeconds * 1000)
    : 0
  stopOtpTimer()
  updateOtpRemaining()
  if (otpExpiresAt.value) {
    otpTimer = setInterval(updateOtpRemaining, 1000)
  }
}

onBeforeUnmount(() => stopOtpTimer())

const handleLogin = async () => {
  loading.value = true
  authStore.clearError()
  
  try {
    const result = await authStore.login({
      username: form.value.username,
      password: form.value.password,
    })
    
    if (result.success) {
      if (result.otpRequired) {
        otpStep.value = true
        otpToken.value = String(result.otpToken || '').trim()
        otpCode.value = ''
        startOtpTimer({ expiresInSeconds: result.expiresIn })
        return
      }
      emit('success')
      closeModal()
    }
  } catch (error) {
    console.error('Login error:', error)
  } finally {
    loading.value = false
  }
}


const handleVerifyOtp = async () => {
  loading.value = true
  authStore.clearError()

  try {
    const result = await authStore.verifyOtp({
      otpToken: otpToken.value,
      code: otpCode.value,
    })

    if (result.success) {
      emit('success')
      closeModal()
    }
  } catch (error) {
    console.error('OTP error:', error)
  } finally {
    loading.value = false
  }
}

const handleResendOtp = async () => {
  loading.value = true
  authStore.clearError()

  try {
    const result = await authStore.login({
      username: form.value.username,
      password: form.value.password,
    })

    if (result.success && result.otpRequired) {
      otpToken.value = String(result.otpToken || '').trim()
      otpCode.value = ''
      startOtpTimer({ expiresInSeconds: result.expiresIn })
    }
  } catch (error) {
    console.error('Resend OTP error:', error)
  } finally {
    loading.value = false
  }
}

const resetOtpStep = () => {
  otpStep.value = false
  otpToken.value = ''
  otpCode.value = ''
  otpExpiresAt.value = 0
  otpRemainingSeconds.value = 0
  stopOtpTimer()
  authStore.clearError()
}
const goToRecovery = () => {
  closeModal()
  router.push('/forgot-password')
}

const switchToRegister = () => {
  emit('switchToRegister')
}

const closeModal = () => {
  authStore.clearError()
  otpStep.value = false
  otpToken.value = ''
  otpCode.value = ''
  otpExpiresAt.value = 0
  otpRemainingSeconds.value = 0
  stopOtpTimer()
  form.value = { username: '', password: '', remember: false }
  showPassword.value = false
  emit('close')
}
</script>
